import React from "react";

import { PlusCircle } from "@hl/base-components/lib/assets/icons.generated";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import {
  BACKGROUND_COLOR,
  TEXT_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { Video } from "@hl/base-components/lib/video";
import { useAuth } from "@hl/shared-features/lib/features/auth";
import {
  ListFormToken,
  TokenMarketplace,
  useListingCartContext,
} from "@hl/shared-features/lib/features/cart/ListingCartProvider";
import { expirationOptions } from "@hl/shared-features/lib/features/cart/useListingCart";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import BidBuyButtons from "@hl/shared-features/lib/features/marketplace/components/BidBuyButtons";
import useMarketplaces from "@hl/shared-features/lib/features/marketplace/hooks/useMarketplaces";
import ManageListingsModal, {
  ManageListingsModalTitle,
} from "@hl/shared-features/lib/features/marketplace/modals/ManageListingsModal";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import { constructDFSGatewayUrl } from "@hl/shared-features/lib/utils/dfs";
import { SvgImageRegex } from "@hl/shared-features/lib/utils/regex";
import {
  Anchor,
  Badge,
  Button,
  createStyles,
  Flex,
  Stack,
  Text,
  Tooltip,
  Transition,
} from "@mantine/core";
import { Link } from "react-router-dom";

import {
  NftContractStandard,
  _CollectionType,
} from "../../apollo/graphql.generated";
import { getDetailPageUrl, getMintPageUrl } from "../../config";

import { shimmerStyles } from "./Collected";

const IMAGE_FIT_SIZE = 284;

const useTokenCardStyles = createStyles(
  (theme, { listed }: { listed: boolean }) => ({
    mediaItem: {
      display: "flex",
      flex: "1 1",
      border: `20px solid ${theme.colors.tableRowBackground[0]}`,
      aspectRatio: "1",
      justifyContent: "center",
      alignItems: "center",
    },
    video: {
      display: "block",
      objectFit: "contain",
      width: "100%",
    },
    link: {
      textDecoration: "none",
    },
    overlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: 2,
      backgroundColor: listed
        ? theme.fn.rgba(theme.colors.secondaryBackground[0], 0.6)
        : undefined,
      transition: "all 0.1s ease",
      "&:hover": {
        backgroundColor: theme.fn.rgba(
          theme.colors.secondaryBackground[0],
          listed ? 0.5 : 0.15
        ),
      },
      "&[data-visible]": {
        opacity: 1,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "0",
        height: "0",
        backgroundColor: theme.fn.rgba(theme.colors.primaryBackground[0], 0.2),
        borderRadius: "50%",
        transform: "translate(-50%, -50%)",
        opacity: 0,
        transition: "all 0.1s ease",
      },
      "&:active::after": {
        width: "120%",
        height: "120%",
        opacity: 1,
        transition: "none",
      },
    },
  })
);

const TokenCard = ({
  token,
  onCompleted = () => null,
  isOwner = false,
}: {
  token: TokenMarketplace;
  onCompleted?: () => void;
  isOwner?: boolean;
}) => {
  const { pushModal } = useModalStack();
  const { foreignMarketplaces } = useMarketplaces();
  const { state, dispatch } = useListingCartContext();
  const { walletAddress } = useAuth();
  const isBeingListed = !!state.tokens.find(
    (el) =>
      el.tokenId === token.tokenId &&
      el.address.toLowerCase() === token.tokenAddress.toLowerCase()
  );
  const { cx, classes, theme } = useTokenCardStyles({ listed: isBeingListed });
  const { collection } = token;
  const isCodeGenMint = collection.type === _CollectionType.GenerativeSeries;
  const isAnySeriesMint =
    isCodeGenMint || collection.type === _CollectionType.Series;
  const collectionLink = getMintPageUrl(collection);
  const tokenLink = getDetailPageUrl(collection, token.tokenId);
  // const offersAboveFloorCount = token.offersAboveFloorCount ?? 0;
  // const { isMobile } = useMantineMediaQueries({ withInitialValues: true });
  const imageUrl = constructDFSGatewayUrl(
    token.imageUrl || token.metadata?.imageUrlOriginal || "",
    true
  );

  const removeToken = () => {
    dispatch({
      type: "REMOVE_TOKEN",
      payload: { tokenId: token.tokenId, address: token.tokenAddress },
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isBeingListed) {
      removeToken();
    } else {
      listToken();
    }
  };

  const domain = token.tokenListing?.source;
  const foreignMarketplace = foreignMarketplaces.find(
    (marketplace) => marketplace.domain === domain
  );
  const foreignMarketplaceName = foreignMarketplace?.name;
  const isForeignListing = !!foreignMarketplace;

  const listToken = () => {
    const newToken: ListFormToken = {
      id: token.id,
      tokenId: token.tokenId,
      chainId: token.chainId,
      address: token.collection.address,
      quantity: 1,
      royaltyBps: 0,
      expirationOption: expirationOptions[5],
      editionId: collection.onchainId?.split(":")[2],
      price: "",
      token,
      relistingId: isForeignListing
        ? token.tokenListing?.reservoirId
        : undefined,
    };
    dispatch({ type: "ADD_TOKENS", payload: [newToken] });
  };
  const isERC1155 = token.collection.standard === NftContractStandard.ERC1155;
  const isUsersListing =
    !!walletAddress &&
    token.tokenListing?.makerAddress.toLowerCase() ===
      walletAddress?.toLowerCase();

  return (
    <Stack
      key={token.id}
      sx={{
        border: `1px solid ${
          isBeingListed ? theme.colors.divider[0] : "transparent"
        }`,
        transition: "border-color 0.2s ease",
      }}
    >
      <Anchor
        className={classes.link}
        href={tokenLink}
        target="_blank"
        pos="relative"
      >
        <Transition
          mounted={state.opened}
          transition="fade"
          duration={200}
          timingFunction="ease"
        >
          {(transitionStyles) => (
            <div
              className={classes.overlay}
              onClick={handleOverlayClick}
              style={transitionStyles}
              data-visible={state.opened || undefined}
            ></div>
          )}
        </Transition>
        {token.balance && (
          <Badge
            color={BACKGROUND_COLOR.QUATERNARY}
            size="xs"
            styles={{
              root: {
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                borderRadius: "999px",
                height: "24px",
                padding: "4px 8px",
                backgroundColor: theme.colors.baseBackground[0],
                outline: `1px solid ${theme.colors.divider[0]}`,
              },
            }}
          >
            <Text size="xs" color={TEXT_COLOR.PRIMARY} tt="lowercase">
              {token.balance} owned
            </Text>
          </Badge>
        )}
        {token.animationUrl && !isCodeGenMint && !collection.imported ? (
          <Video
            className={cx([classes.video, classes.mediaItem])}
            autoPlay
            loop
            muted
            playsInline
          >
            <source src={token.animationUrl} />
          </Video>
        ) : (
          <ResizedImage
            className={classes.mediaItem}
            src={imageUrl}
            fit={SvgImageRegex.test(imageUrl ?? "") ? "fill" : "contain"}
            fitSize={IMAGE_FIT_SIZE}
            aspectRatio="1"
          />
        )}
      </Anchor>
      <Flex justify="space-between" px="xs" pb="xs">
        <Stack spacing={0}>
          <Text component={Link} to={tokenLink} weight={WEIGHT_BOLD} size="sm">
            {isERC1155
              ? token.collection.name
              : isAnySeriesMint
              ? token.name
              : `#${token.tokenRevealId ?? token.tokenId}`}
          </Text>
          {!isERC1155 && (
            <Text
              component={Link}
              to={collectionLink}
              size="sm"
              color={theme.colors.secondaryText[0]}
            >
              {token.collection.name}
            </Text>
          )}
          {/*{(isListed || offersAboveFloorCount > 0) && (*/}
          {/*  <Text size="sm" color={theme.colors.secondaryText[0]}>*/}
          {/*    {token.tokenListing && "Listed for sale"}*/}
          {/*    {offersAboveFloorCount > 0 && isListed && " · "}*/}
          {/*    {offersAboveFloorCount > 0 &&*/}
          {/*      `${token.offersAboveFloorCount} offer${*/}
          {/*        token.offersAboveFloorCount === 1 ? "" : "s"*/}
          {/*      } above floor`}*/}
          {/*  </Text>*/}
          {/*)}*/}
        </Stack>
        <Flex>
          {isOwner ? (
            !state.opened ? (
              !isUsersListing || isForeignListing ? (
                <Tooltip
                  label={`Listing detected on ${
                    foreignMarketplaceName || domain
                  }. Re-list for 0% on Highlight.`}
                  disabled={!domain}
                >
                  <Button
                    size="xs"
                    color={SECONDARY_COLOR}
                    onClick={() => listToken()}
                    styles={{
                      // @ts-ignore
                      root: {
                        ...(isForeignListing ? shimmerStyles(true) : {}),
                        transition: "background-color 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: theme.colors.secondaryButton[1],
                        },
                      },
                      label: {
                        position: "relative",
                        zIndex: 1,
                      },
                    }}
                  >
                    {isForeignListing ? "Relist to save" : "List for sale"}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  size="xs"
                  color={OUTLINE_COLOR}
                  w="100%"
                  px={8}
                  onClick={() =>
                    pushModal(
                      <ManageListingsModal
                        collectionData={{
                          id: collection?.id,
                          onchainId: collection?.onchainId,
                          address: collection?.address || "",
                          chainId: token.chainId,
                          name: collection?.name || "",
                          marketplaceId: collection?.marketplaceId,
                          standard: collection?.standard,
                        }}
                        tokenId={token.tokenId ?? ""}
                        onCompleted={onCompleted}
                      />,
                      {
                        size: "100%",
                        title: (
                          <ManageListingsModalTitle
                            tokenName={token.name}
                            collectionName={collection?.name}
                          />
                        ),
                      }
                    )
                  }
                >
                  Manage listings
                </Button>
              )
            ) : (
              <Flex
                gap={4}
                align="center"
                sx={{ alignSelf: "start", cursor: "pointer" }}
                onClick={isBeingListed ? removeToken : listToken}
              >
                <Text size="sm" fw={WEIGHT_BOLD}>
                  {isBeingListed ? "Remove from list" : "Add to list"}{" "}
                </Text>
                <PlusCircle
                  width={16}
                  color={
                    isBeingListed
                      ? theme.colors.errorStatus[0]
                      : theme.colors.successStatus[0]
                  }
                  style={{
                    transition: "all 0.1s ease-in-out",
                    rotate: isBeingListed ? "45deg" : "0deg",
                  }}
                  height={16}
                />
              </Flex>
            )
          ) : (
            <BidBuyButtons
              token={token}
              collection={{ ...token.collection, chainId: token.chainId }}
              onCompleted={onCompleted}
            />
          )}
        </Flex>
      </Flex>
    </Stack>
  );
};

export default TokenCard;
